import { getReadableFileSize } from "../file/size.ts";
import mime from "mime";

/** is the extension valid? */
export const isValidExtension = (ext: string) =>
  new RegExp(/^[a-zA-Z0-9]+$/).test(ext);

/** does a name or URI have a valid extension? */
export const hasValidExtension = (ext: string) =>
  new RegExp(/\.[a-zA-Z0-9]+$/).test(ext);

/** is the URI pointing to a file on a mobile device? */
export const isMobileDeviceFileUri = (uri: string) =>
  uri.startsWith("file://") || uri.startsWith("content://");

export const isMobileDeviceContentFileUri = (uri: string) =>
  uri.startsWith("content://");

/** is the URI pointing to a file in a remote location across the network? */
export const isRemoteUri = (uri: string) => uri.startsWith("http");

/**
 * Validates if a File passes the provided size restrictions. Meant to be used with react-hook-form.
 * @param file a valid File object
 * @param param1.max provide the maximum file size in bytes that the file being validated can be (inclusive) check applied last.
 * @param param1.min provide the minimum file size in bytes that the file being validated can be (inclusive) check applied first.
 * @returns boolean true if validation passed, error message otherwise
 */
export function validateFileSize(
  file: File | null,
  { min, max }: { min?: number; max?: number }
) {
  if (!file) return true;

  if (min !== undefined && min >= 0 && file?.size < min) {
    return `The file is too small. The minimum file size is ${getReadableFileSize(
      min
    )}`;
  }

  if (max !== undefined && max >= 0 && file?.size > max) {
    return `The file is too large. The maximum file size is ${getReadableFileSize(
      max
    )}`;
  }

  return true;
}

/**
 * Validates if a File's MIME type is one of the provided MIME types
 * @param file a valid File object
 * @param acceptedMimeTypes the accept file types - same format used in the HTML input "accept" attribute
 * @returns boolean true if validation passed, error message otherwise
 */
export function validatedFileType(
  file: File | null,
  acceptedMimeTypes?: HTMLInputElement["accept"]
) {
  if (!file) return true;

  if (!acceptedMimeTypes || typeof acceptedMimeTypes !== "string") {
    return true;
  }

  const acceptedFileExtensions = acceptedMimeTypes
    .split(",")
    .map((type) => mime.getExtension(type.trim()))
    .filter((type) => Boolean(type));
  const fileMimeType = file.type;

  if (acceptedMimeTypes && !fileMimeType) {
    return "Unknown file type not accepted";
  }

  const isValidFileType = acceptedMimeTypes.includes(fileMimeType);

  if (!isValidFileType) {
    return `File must be one of: ${acceptedFileExtensions.join(", ")}`;
  }

  return true;
}
